import React from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Button, Container, Row, Col } from 'react-bootstrap';
import { withTranslation } from 'react-i18next';
import BaseCourseContent from './BaseCourseContent';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { trackTrainingEvent } from '../../trainingTelemetry';

// Worker serwowany Z NASZEJ instalacji, nie z cdnjs.cloudflare.com. Klient on-premise
// (Grupa Azoty, ROCKWOOL) bywa odciety od internetu, a jego firewall dropuje ruch po cichu —
// slajdy nie renderowalyby sie wcale, a objawem byloby "wiecznie sie wczytuje" zamiast bledu.
// Plik trafia do public/ przy kazdym buildzie (npm run prebuild -> scripts/kopiuj-worker-pdf.js),
// zawsze w wersji zgodnej z API pdf.js uzywanym przez react-pdf.
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;

/**
 * Component for handling PDF content in courses
 */
class PDFCourseContent extends BaseCourseContent {
    static canHandle(url) {
        if (!url) return false;
        return url.toLowerCase().endsWith('.pdf');
    }

    // pageFrom/pageTo (1-based, inclusive) optionally restrict the visible range of
    // the PDF to a single scenario-step fragment. Undefined => whole document, same
    // as before this was introduced.
    getPageBounds = (numPages) => {
        const { pageFrom, pageTo } = this.props;
        const total = numPages || 1;
        const from = pageFrom ? Math.max(1, pageFrom) : 1;
        const to = pageTo ? Math.min(total, pageTo) : total;
        return { from, to: Math.max(from, to) };
    };

    state = {
        currentPage: 1,
        numPages: null,
        containerDimensions: { width: 0, height: 0 },
        navigationHeight: 0,
        pageAspect: null
    };

    containerRef = React.createRef();

    onDocumentLoadSuccess = ({ numPages }) => {
        const { from } = this.getPageBounds(numPages);
        trackTrainingEvent('pdf_loaded', 'pdf-loaded', 'PDF loaded', {
            url: contentUrl(this.props.content),
            numPages,
            fromPage: from,
            toPage: this.getPageBounds(numPages).to,
        });
        this.setState({ numPages, currentPage: from });
    };

    onDocumentLoadError = (error) => {
        console.error("Error loading PDF document:", error);
        trackTrainingEvent('pdf_error', 'pdf-error', 'PDF loading error', {
            url: contentUrl(this.props.content),
            message: error.message,
        });
    };

    handlePreviousPage = () => {
        const { from } = this.getPageBounds(this.state.numPages);
        trackTrainingEvent('pdf_page_previous', 'pdf-page-previous', 'PDF previous page', {
            url: contentUrl(this.props.content),
            currentPage: this.state.currentPage,
        });
        this.setState(prevState => ({
            currentPage: Math.max(prevState.currentPage - 1, from)
        }));
    };

    handleNextPage = () => {
        const { to } = this.getPageBounds(this.state.numPages);
        trackTrainingEvent('pdf_page_next', 'pdf-page-next', 'PDF next page', {
            url: contentUrl(this.props.content),
            currentPage: this.state.currentPage,
            toPage: to,
        });
        this.setState(prevState => ({
            currentPage: Math.min(prevState.currentPage + 1, to)
        }));
    };

    updateContainerDimensions = () => {
        if (this.containerRef.current) {
            // visualViewport.height to obszar NAPRAWDE widoczny (bez paska adresu,
            // z uwzglednieniem pinch-zoomu); window.innerHeight bywa wiekszy i to on
            // powodowal, ze slajd byl liczony na wiecej miejsca, niz uzytkownik widzi.
            const windowHeight = Math.min(
                window.innerHeight,
                window.visualViewport?.height || window.innerHeight
            );
            const navigationHeight = document.querySelector('.fixed-bottom')?.clientHeight || 0;
            const padding = 45;
            // clientWidth kolumny zawiera JEJ padding, wiec strona PDF ustawiona na te
            // wartosc byla szersza od miejsca, ktore ma do dyspozycji, i wychodzila poza
            // ekran (zmierzone: +12 px na 390 px). Odejmujemy padding kolumny.
            const style = window.getComputedStyle(this.containerRef.current);
            const horizontalPadding =
                parseFloat(style.paddingLeft || 0) + parseFloat(style.paddingRight || 0);
            const availableWidth = Math.max(0, this.containerRef.current.clientWidth - horizontalPadding);

            const height = windowHeight - navigationHeight - padding;
            const { containerDimensions: poprzednie, navigationHeight: poprzedniPasek } = this.state;
            // ResizeObserver reaguje takze na zmiany wywolane naszym wlasnym setState —
            // bez tego strażnika powstalaby petla przerysowan.
            if (
                poprzednie.width === availableWidth &&
                poprzednie.height === height &&
                poprzedniPasek === navigationHeight
            ) {
                return;
            }

            this.setState({
                containerDimensions: {
                    width: availableWidth,
                    height,
                },
                // Pasek nawigacji na telefonie zawija sie do trzech wierszy (zmierzone 100 px
                // przy 390 px szerokosci), a tresc rezerwowala pod niego sztywne 45 px —
                // przyciski zaslanialy dol slajdu. Rezerwujemy tyle, ile pasek naprawde zajmuje.
                navigationHeight,
            });
        }
    };

    /**
     * Proporcje strony sa znane dopiero po jej wczytaniu — zapamietujemy je, zeby
     * moc dopasowac slajd takze do WYSOKOSCI kontenera.
     */
    onPageLoadSuccess = (page) => {
        const { originalWidth, originalHeight } = page;
        if (!originalWidth || !originalHeight) {
            return;
        }
        const proporcje = originalWidth / originalHeight;
        if (Math.abs((this.state.pageAspect || 0) - proporcje) > 0.001) {
            this.setState({ pageAspect: proporcje });
        }
        // Do tego momentu pasek nawigacji jest juz wyrenderowany — pomiar z mountu
        // (navigationHeight = 0) trzeba poprawic, inaczej slajd zostaje za duzy.
        this.updateContainerDimensions();
    };

    /**
     * `<Page width>` ustawia SZEROKOSC, a wysokosc wynika z proporcji strony. Slajd 16:9
     * rozciagniety na cala szerokosc telefonu trzymanego poziomo wychodzil wyzszy niz
     * kontener (zmierzone: 461 px tresci w oknie 292 px) i dolna trzecia czesc byla
     * ucieta — kontener ma overflow:hidden, wiec nie dalo sie jej nawet doscrollowac.
     * Dlatego szerokosc ograniczamy tak, by strona zmiescila sie w OBU wymiarach.
     */
    getFittedPageWidth = () => {
        const { containerDimensions, pageAspect } = this.state;
        const dostepnaSzerokosc = containerDimensions.width;
        const dostepnaWysokosc = containerDimensions.height;

        if (!pageAspect || !dostepnaWysokosc || dostepnaWysokosc <= 0) {
            return dostepnaSzerokosc;
        }
        return Math.max(1, Math.min(dostepnaSzerokosc, dostepnaWysokosc * pageAspect));
    };

    componentDidMount() {
        this.updateContainerDimensions();
        window.addEventListener('resize', this.updateContainerDimensions);
        // Obrot telefonu zmienia wymiary, ale Safari na iOS potrafi zglosic
        // orientationchange ZANIM przeliczy innerHeight — stad dodatkowy przebieg.
        window.addEventListener('orientationchange', this.handleOrientationChange);

        // `resize` NIE wystarcza. Pomiar z componentDidMount jest robiony, zanim
        // pasek `.fixed-bottom` sie wyrenderuje (navigationHeight = 0 => wysokosc
        // zawyzona) i zanim kolumna dostanie ostateczna szerokosc. Slajd policzony
        // z tych zawyzonych liczb jest wiekszy niz kontener z overflow:hidden i jest
        // UCINANY z prawej i z dolu, a zdarzenie resize nigdy nie przychodzi, wiec
        // stan zostaje bledny do konca szkolenia (zgloszenie ArcelorMittal 13.08.2026,
        // Samsung, szkolenie "Przestrzenie zamkniete"). ResizeObserver lapie kazda
        // zmiane rozmiaru kontenera i paska, takze te bez zdarzenia resize.
        if (typeof ResizeObserver !== 'undefined') {
            this.resizeObserver = new ResizeObserver(this.updateContainerDimensions);
            if (this.containerRef.current) {
                this.resizeObserver.observe(this.containerRef.current);
            }
            const pasek = document.querySelector('.fixed-bottom');
            if (pasek) {
                this.resizeObserver.observe(pasek);
            }
        }

        // Chowanie/pokazywanie paska adresu przegladarki mobilnej i pinch-zoom zmieniaja
        // widoczny obszar BEZ zdarzenia `resize` na window.
        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', this.updateContainerDimensions);
        }
    }

    handleOrientationChange = () => {
        this.updateContainerDimensions();
        setTimeout(this.updateContainerDimensions, 300);
    };

    componentWillUnmount() {
        window.removeEventListener('resize', this.updateContainerDimensions);
        window.removeEventListener('orientationchange', this.handleOrientationChange);
        if (window.visualViewport) {
            window.visualViewport.removeEventListener('resize', this.updateContainerDimensions);
        }
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
    }

    render() {
        // `continueLabelKey` — patrz komentarz w VideoContent.js: domyslnie `start_exam` (tryb
        // logowania pracownika, po materiale zawsze jest test), stanowisko rejestracyjne podaje
        // wlasny klucz (`finish_training_title`), bo egzaminu tam nie ma z definicji trybu.
        const { content, onExamStart, t, isLastMaterial, continueLabelKey } = this.props;
        const { currentPage, numPages, containerDimensions, navigationHeight } = this.state;
        const { from: fromPage, to: toPage } = this.getPageBounds(numPages);
        // "Next" while more materials follow, `continueLabelKey` (default "Start exam") on the last one.
        const continueLabel = isLastMaterial === false ? t('next_material') : t(continueLabelKey || 'start_exam');

        if (!content || !content.url) {
            return <div>{t('pdf_not_available')}</div>;
        }

        return (
            <Container
                fluid
                className="PDFCourseContent"
                style={{ paddingBottom: `${Math.max(45, navigationHeight + 13)}px` }}
            >
                <Row>
                    <Col md={12} ref={this.containerRef} className="d-flex justify-content-center">
                        <div
                            className="d-flex justify-content-center align-items-center"
                            style={{ height: `${containerDimensions.height}px`, width: `${containerDimensions.width}px`, overflow: 'hidden' }}
                        >
                            <Document
                                file={content.url}
                                onLoadSuccess={this.onDocumentLoadSuccess}
                                onLoadError={this.onDocumentLoadError}
                                loading={<div>{t('loading')}</div>}
                                error={<div>{t('error_loading_pdf')}</div>}
                            >
                                <Page
                                    pageNumber={currentPage}
                                    width={this.getFittedPageWidth()}
                                    onLoadSuccess={this.onPageLoadSuccess}
                                />
                            </Document>
                        </div>
                    </Col>
                </Row>
                {/* g-0: .fixed-bottom rozciaga element na left:0/right:0, a ujemne marginesy
                    .row dokladaly do tego szerokosc rynny — pasek byl o 24 px szerszy od ekranu.
                    xs={4}: kolumny md rozjezdzaly sie na telefonie na trzy wiersze. */}
                <Row className="fixed-bottom bg-light g-0 px-2 py-2 align-items-center">
                    <Col xs={4} className="d-flex align-items-center">
                        <Button
                            variant="danger"
                            size="sm"
                            onClick={this.handlePreviousPage}
                            disabled={currentPage <= fromPage}
                        >
                            {t('previous_slide')}
                        </Button>
                    </Col>
                    <Col xs={4} className="text-center d-flex align-items-center justify-content-center">
                        {numPages && (
                            <span className="small">
                                {t('page')} {currentPage} {t('of')} {toPage}
                            </span>
                        )}
                    </Col>
                    <Col xs={4} className="d-flex align-items-center justify-content-end">
                        {currentPage < toPage && (
                            <Button variant="danger" size="sm" onClick={this.handleNextPage}>
                                {t('next_slide')}
                            </Button>
                        )}
                        {currentPage === toPage && (
                            <Button variant="danger" size="sm" onClick={onExamStart}>
                                {continueLabel}
                            </Button>
                        )}
                    </Col>
                </Row>
            </Container>
        );
    }
}

function contentUrl(content) {
    return content && content.url ? content.url : null;
}

export default withTranslation()(PDFCourseContent);
